import { useId, useState, type ReactNode } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Card } from '../Card/Card';
import { Icon } from '../Icon';

export type DisclosureCardProps = {
  /** The card's name: what the tool or section is. */
  title: ReactNode;
  /** One line under the title saying what opening it gets you. */
  description?: ReactNode;
  /** Heading level of the title so the card sits in the page outline. */
  headingLevel?: 2 | 3 | 4;
  /** Controlled state. Leave out to let the card keep its own. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  id?: string;
};

/**
 * A card that opens in place: title and one-line description always visible, the tool or section behind a
 * press. For optional tools on a page (a job ad check, saved versions) that should not push the page down until
 * wanted. The content is hidden (out of the tab order and the accessibility tree) while closed but stays mounted, so what was typed survives. For FAQ-style lists of
 * questions use `Accordion`.
 */
export function DisclosureCard({
  title,
  description,
  headingLevel = 3,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  children,
  className,
  id,
}: DisclosureCardProps) {
  const [inner, setInner] = useState(defaultOpen);
  const open = openProp ?? inner;
  const auto = `dc${useId().replace(/:/g, '')}`;
  const Heading = `h${headingLevel}` as 'h3';
  const toggle = () => {
    const next = !open;
    if (openProp === undefined) setInner(next);
    onOpenChange?.(next);
  };
  return (
    <Card as="section" id={id} padding="none" className={className}>
      <Heading className="type-subheading text-jc-ink">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${auto}-body`}
          onClick={toggle}
          className="flex w-full items-center justify-between gap-4 rounded-jc-container p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jc-focus md:px-6"
        >
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="block">{title}</span>
            {description ? <span className="font-sans text-sm font-normal text-jc-ink-secondary">{description}</span> : null}
          </span>
          <Icon
            icon={CaretDown}
            size="md"
            className={cx(
              'shrink-0 text-jc-ink-secondary transition-transform duration-(--jc-duration-fast) motion-reduce:transition-none',
              open && 'rotate-180',
            )}
          />
        </button>
      </Heading>
      <div id={`${auto}-body`} hidden={!open}>
        <div className="border-t border-jc-border p-5 md:p-6">{children}</div>
      </div>
    </Card>
  );
}
